import { useEffect, useMemo, useState } from "react";
import { http } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Unlock, UserPlus, Trash2, KeyRound, Search, ShieldAlert, Smartphone } from "lucide-react";

const ROLES = ["super_admin","admin","mentor","eleve"];

export default function UsersAdmin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedStudents, setSelectedStudents] = useState(() => new Set());
  const [bulkPanel, setBulkPanel] = useState(false);
  const [bulkSelectedCourses, setBulkSelectedCourses] = useState(() => new Set());
  const [bulkCourseSearch, setBulkCourseSearch] = useState("");
  const canCreate = ["super_admin","admin"].includes(user?.role);
  const isMentor = user?.role === "mentor";

  async function refresh() { const r = await http.get("/users"); setUsers(r.data); }
  useEffect(() => { refresh(); http.get("/courses").then(r => setCourses(r.data)); }, []);

  async function unlockDevice(id, isLocked, name) {
    if (!isLocked) {
      if (!window.confirm(`Réinitialiser l'appareil de ${name} ?\n\nL'élève pourra se reconnecter depuis un nouveau téléphone/ordinateur lors de sa prochaine connexion.`)) return;
    }
    try { await http.post(`/users/${id}/unlock`); toast.success(isLocked ? "Élève débloqué" : "Appareil réinitialisé"); refresh(); }
    catch { toast.error("Échec déblocage"); }
  }
  async function assignMentor(studentId, mentorId) {
    await http.patch(`/users/${studentId}`, { mentor_id: mentorId || null });
    toast.success("Mentor assigné"); refresh();
  }
  async function deleteUser(id) {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try { await http.delete(`/users/${id}`); toast.success("Supprimé"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }
  async function unlockCourse(studentId, courseId) {
    try { await http.post(`/courses/${courseId}/unlock?student_id=${studentId}`); toast.success("Cours débloqué"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function bulkUnlockOne(studentId, courseIds) {
    try {
      const body = courseIds === null
        ? { student_ids: [studentId], unlock_all: true }
        : { student_ids: [studentId], course_ids: courseIds };
      const r = await http.post("/admin/courses/bulk-unlock", body);
      toast.success(`${r.data.inserted} cours débloqué(s)${r.data.skipped ? ` · ${r.data.skipped} déjà débloqué(s)` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de déblocage"); }
  }

  async function bulkUnlockMany(studentIds, opts) {
    // opts = { course_ids?, unlock_all? }
    try {
      const body = { student_ids: studentIds, ...opts };
      const r = await http.post("/admin/courses/bulk-unlock", body);
      toast.success(`${r.data.inserted} déblocage(s) · ${r.data.students} élève(s) × ${r.data.courses} cours`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de déblocage groupé"); }
  }

  const mentors = users.filter(u => u.role === "mentor");

  const filteredUsers = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users
      .filter(u => {
        if (roleFilter === "locked") { if (!u.is_locked) return false; }
        else if (roleFilter !== "all" && u.role !== roleFilter) return false;
        if (s && !((u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s))) return false;
        return true;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
  }, [users, search, roleFilter]);

  const lockedCount = users.filter(u => u.is_locked).length;

  return (
    <div className="space-y-6" data-testid="users-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-headings font-black text-3xl md:text-4xl text-[#1a2332]">Utilisateurs</h1>
          <p className="mt-1 text-[#5b6577]">{users.length} comptes · {user?.role === "mentor" ? "Vos élèves" : "Gestion complète"}</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowNew(true)} data-testid="new-user-btn"
            className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-5 py-2.5 shadow-sm">
            <UserPlus className="w-4 h-4" /> Nouvel utilisateur
          </button>
        )}
      </div>

      {showNew && canCreate && <NewUserForm mentors={mentors} onClose={() => setShowNew(false)} onDone={refresh} />}

      {/* Search + role filter */}
      <div className="bg-white border border-black/5 shadow-sm rounded-2xl p-3 flex items-center gap-3 flex-wrap" data-testid="users-search-bar">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-[#f8faff] border border-[#e5eaf3] rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-[#5b6577]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom ou email…"
            className="flex-1 bg-transparent outline-none text-sm text-[#1a2332]" data-testid="users-search-input" />
          {search && (
            <button onClick={() => setSearch("")} className="text-xs text-[#9aa5bc] hover:text-[#e21b3c]" data-testid="users-search-clear">×</button>
          )}
        </div>
        {!isMentor && (
          <div className="flex gap-1 bg-[#f8faff] border border-[#e5eaf3] rounded-xl p-1 flex-wrap" data-testid="users-role-filter">
            {[
              { k: "all", l: `Tous · ${users.length}` },
              { k: "eleve", l: `Élèves · ${users.filter(u=>u.role==="eleve").length}` },
              { k: "mentor", l: `Mentors · ${users.filter(u=>u.role==="mentor").length}` },
              { k: "admin", l: `Admin · ${users.filter(u=>u.role==="admin"||u.role==="super_admin").length}` },
            ].map(t => (
              <button key={t.k} onClick={() => setRoleFilter(t.k === "admin" ? "admin" : t.k)} data-testid={`role-tab-${t.k}`}
                className={`text-xs font-headings font-bold rounded-lg px-3 py-1.5 ${roleFilter === t.k ? "bg-[#1a2332] text-white" : "text-[#5b6577] hover:bg-white"}`}>
                {t.l}
              </button>
            ))}
            {lockedCount > 0 && (
              <button onClick={() => setRoleFilter("locked")} data-testid="role-tab-locked"
                className={`inline-flex items-center gap-1 text-xs font-headings font-bold rounded-lg px-3 py-1.5 ${roleFilter === "locked" ? "bg-[#e21b3c] text-white" : "text-[#e21b3c] hover:bg-[#e21b3c]/10 animate-pulse"}`}
                title="Élèves bloqués pour usage de plusieurs appareils">
                <ShieldAlert className="w-3 h-3" /> Bloqués · {lockedCount}
              </button>
            )}
          </div>
        )}
        <span className="text-xs text-[#9aa5bc]">{filteredUsers.length} résultat{filteredUsers.length > 1 ? "s" : ""}</span>
      </div>

      <div className="bg-white border border-black/5 shadow-sm rounded-2xl overflow-hidden">
        {selectedStudents.size > 0 && (
          <div className="px-4 py-3 bg-[#FF8C42]/10 border-b border-[#FF8C42]/20 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
            <span className="text-sm font-headings font-bold text-[#1a2332]">{selectedStudents.size} élève(s) sélectionné(s)</span>
            <button onClick={() => setBulkPanel(true)} data-testid="open-bulk-unlock"
              className="inline-flex items-center gap-1 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs">
              <Unlock className="w-3 h-3" /> Débloquer des cours
            </button>
            <button onClick={async () => {
              if (!window.confirm(`Débloquer LES 36 COURS pour ${selectedStudents.size} élève(s) ?`)) return;
              await bulkUnlockMany(Array.from(selectedStudents), { unlock_all: true });
              setSelectedStudents(new Set());
            }} data-testid="bulk-unlock-all-students"
              className="inline-flex items-center gap-1 bg-[#00a86b] hover:bg-[#008855] text-white font-headings font-bold rounded-lg px-3 py-1.5 text-xs">
              <Unlock className="w-3 h-3" /> Tout débloquer (36 cours)
            </button>
            <button onClick={() => setSelectedStudents(new Set())}
              className="text-xs text-[#5b6577] hover:text-[#e21b3c] ml-auto" data-testid="clear-bulk-selection">
              Tout désélectionner ×
            </button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-[#f8faff] text-xs uppercase tracking-wide text-[#5b6577]">
            <tr>
              {canCreate && (
                <th className="p-3 w-10">
                  <input type="checkbox"
                    checked={filteredUsers.filter(u=>u.role==="eleve").length>0 && filteredUsers.filter(u=>u.role==="eleve").every(u => selectedStudents.has(u.id))}
                    onChange={(e) => {
                      const next = new Set(selectedStudents);
                      const eleves = filteredUsers.filter(u => u.role === "eleve");
                      if (e.target.checked) eleves.forEach(u => next.add(u.id));
                      else eleves.forEach(u => next.delete(u.id));
                      setSelectedStudents(next);
                    }}
                    data-testid="select-all-students" />
                </th>
              )}
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Rôle</th>
              <th className="text-left p-3">Mentor</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 && (
              <tr><td colSpan={canCreate ? 6 : 5} className="p-8 text-center text-[#5b6577] text-sm">Aucun utilisateur trouvé.</td></tr>
            )}
            {filteredUsers.map((u) => (
              <tr key={u.id} className={`border-t border-black/5 ${selectedStudents.has(u.id) ? "bg-[#FF8C42]/5" : ""}`} data-testid={`user-row-${u.id}`}>
                {canCreate && (
                  <td className="p-3">
                    {u.role === "eleve" && (
                      <input type="checkbox" checked={selectedStudents.has(u.id)}
                        onChange={() => {
                          const next = new Set(selectedStudents);
                          if (next.has(u.id)) next.delete(u.id); else next.add(u.id);
                          setSelectedStudents(next);
                        }}
                        data-testid={`select-student-${u.id}`} />
                    )}
                  </td>
                )}
                <td className="p-3 font-headings font-semibold text-[#1a2332]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{u.name}</span>
                    {u.is_locked && (
                      <span className="inline-flex items-center gap-1 text-xs bg-[#e21b3c] text-white px-2 py-0.5 rounded animate-pulse"
                        title="Compte verrouillé après détection de plusieurs appareils">
                        <ShieldAlert className="w-3 h-3" /> bloqué
                      </span>
                    )}
                    {u.role === "eleve" && typeof u.courses_total === "number" && u.courses_total > 0 && (
                      <UserProgressBar unlocked={u.courses_unlocked || 0} total={u.courses_total} />
                    )}
                  </div>
                </td>
                <td className="p-3 text-[#5b6577]">{u.email}</td>
                <td className="p-3"><span className="text-[#00a86b] text-xs font-semibold">{u.role}</span></td>
                <td className="p-3">
                  {u.role === "eleve" && canCreate ? (
                    <select value={u.mentor_id || ""} onChange={(e) => assignMentor(u.id, e.target.value)}
                      data-testid={`mentor-select-${u.id}`}
                      className="bg-white border border-black/10 rounded-lg px-2 py-1 text-[#1a2332] text-xs">
                      <option value="">—</option>
                      {mentors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  ) : <span className="text-[#9aa5bc] text-xs">—</span>}
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1.5">
                    {u.role === "eleve" && <UnlockCoursesMenu user={u} courses={courses} onUnlock={unlockCourse} onBulkUnlock={bulkUnlockOne} />}
                    {u.role === "eleve" && !isMentor && (
                      u.is_locked ? (
                        <button onClick={() => unlockDevice(u.id, true, u.name)} data-testid={`unlock-device-${u.id}`}
                          className="inline-flex items-center gap-1 bg-[#e21b3c] hover:bg-[#c41632] text-white font-headings font-bold rounded-lg px-2 py-1 text-xs animate-pulse"
                          title="Cet élève a été bloqué après tentative de connexion depuis plusieurs appareils. Cliquez pour autoriser à nouveau l'accès.">
                          <KeyRound className="w-3 h-3" /> Débloquer
                        </button>
                      ) : (
                        <button onClick={() => unlockDevice(u.id, false, u.name)} data-testid={`reset-device-${u.id}`}
                          className="inline-flex items-center gap-1 bg-[#1368ce]/10 hover:bg-[#1368ce]/20 text-[#1368ce] rounded-lg px-2 py-1 text-xs"
                          title="Réinitialiser l'appareil autorisé (ex. l'élève a changé de téléphone)">
                          <Smartphone className="w-3 h-3" /> Appareil
                        </button>
                      )
                    )}
                    {canCreate && u.id !== user?.id && (
                      <button onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`}
                        className="inline-flex items-center gap-1 bg-[#e21b3c]/10 hover:bg-[#e21b3c]/20 text-[#e21b3c] rounded-lg px-2 py-1 text-xs">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk unlock modal */}
      {bulkPanel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBulkPanel(false)} data-testid="bulk-unlock-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-black/5 bg-gradient-to-r from-[#FF8C42]/10 to-white">
              <div className="font-headings font-black text-xl text-[#1a2332]">Déblocage groupé</div>
              <div className="text-sm text-[#5b6577] mt-1">
                {selectedStudents.size} élève(s) sélectionné(s) · Choisissez les cours à débloquer
              </div>
            </div>
            <div className="p-4 border-b border-black/5">
              <div className="flex items-center gap-2 bg-[#f8faff] border border-[#e5eaf3] rounded-lg px-3 py-2">
                <Search className="w-3.5 h-3.5 text-[#5b6577]" />
                <input autoFocus value={bulkCourseSearch} onChange={(e) => setBulkCourseSearch(e.target.value)}
                  placeholder="Rechercher un cours…"
                  className="flex-1 bg-transparent outline-none text-sm text-[#1a2332]"
                  data-testid="bulk-search-input" />
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {[1,2,3,4].map(comp => {
                  const compCourses = courses.filter(c => c.competency === comp);
                  return (
                    <button key={comp} onClick={() => {
                      const next = new Set(bulkSelectedCourses);
                      compCourses.forEach(c => next.add(c.id));
                      setBulkSelectedCourses(next);
                    }}
                      className="text-[10px] font-headings font-bold bg-[#00a86b]/10 hover:bg-[#00a86b]/20 text-[#00a86b] rounded-md px-2 py-1"
                      data-testid={`bulk-comp-${comp}`}>
                      + Compétence {comp} ({compCourses.length})
                    </button>
                  );
                })}
                <button onClick={() => setBulkSelectedCourses(new Set(courses.map(c => c.id)))}
                  className="text-[10px] font-headings font-bold bg-[#FF8C42]/10 hover:bg-[#FF8C42]/20 text-[#FF8C42] rounded-md px-2 py-1">
                  + Tous (36)
                </button>
                {bulkSelectedCourses.size > 0 && (
                  <button onClick={() => setBulkSelectedCourses(new Set())}
                    className="text-[10px] text-[#5b6577] hover:text-[#e21b3c] ml-auto">
                    Réinitialiser ×
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {[1,2,3,4].map(comp => {
                const list = courses.filter(c => c.competency === comp)
                  .sort((a,b) => a.order - b.order)
                  .filter(c => {
                    const s = bulkCourseSearch.trim().toLowerCase();
                    if (!s) return true;
                    const code = `c${c.competency}-${String(c.order).padStart(2,"0")}`.toLowerCase();
                    return code.includes(s) || (c.title||"").toLowerCase().includes(s);
                  });
                if (list.length === 0) return null;
                return (
                  <div key={comp}>
                    <div className="text-[10px] font-headings font-bold text-[#9aa5bc] uppercase tracking-wide px-2 py-1">Compétence {comp}</div>
                    {list.map(c => {
                      const sel = bulkSelectedCourses.has(c.id);
                      return (
                        <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${sel ? "bg-[#FF8C42]/10" : "hover:bg-[#f8faff]"}`}
                          data-testid={`bulk-course-row-${c.id}`}>
                          <input type="checkbox" checked={sel} onChange={() => {
                            const next = new Set(bulkSelectedCourses);
                            if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                            setBulkSelectedCourses(next);
                          }} />
                          <span className="font-mono text-[10px] text-[#9aa5bc]">{`C${c.competency}-${String(c.order).padStart(2,"0")}`}</span>
                          <span className="text-[#1a2332]">{c.title}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t border-black/5 bg-[#f8faff] flex items-center gap-3 flex-wrap">
              <span className="text-sm text-[#5b6577]">{bulkSelectedCourses.size} cours × {selectedStudents.size} élèves = <strong className="text-[#FF8C42]">{bulkSelectedCourses.size * selectedStudents.size}</strong> déblocages</span>
              <button onClick={async () => {
                if (bulkSelectedCourses.size === 0) { toast.error("Sélectionnez au moins un cours."); return; }
                await bulkUnlockMany(Array.from(selectedStudents), { course_ids: Array.from(bulkSelectedCourses) });
                setBulkSelectedCourses(new Set()); setBulkPanel(false); setSelectedStudents(new Set());
              }} data-testid="bulk-confirm-btn"
                className="ml-auto bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm">
                Débloquer
              </button>
              <button onClick={() => setBulkPanel(false)}
                className="bg-white border border-black/5 text-[#1a2332] rounded-xl px-4 py-2.5 text-sm">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UnlockCoursesMenu({ user, courses, onUnlock, onBulkUnlock }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [alreadyUnlocked, setAlreadyUnlocked] = useState(() => new Set());
  const [loadingUnlocks, setLoadingUnlocks] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingUnlocks(true);
    http.get(`/admin/users/${user.id}/unlocks`)
      .then(r => { if (!cancelled) setAlreadyUnlocked(new Set(r.data.course_ids || [])); })
      .catch(() => { if (!cancelled) setAlreadyUnlocked(new Set()); })
      .finally(() => { if (!cancelled) setLoadingUnlocks(false); });
    return () => { cancelled = true; };
  }, [open, user.id]);

  function toggle(id) {
    if (alreadyUnlocked.has(id)) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }

  function close() { setOpen(false); setSelected(new Set()); setQ(""); }

  async function applySelection() {
    if (selected.size === 0) return;
    await onBulkUnlock(user.id, Array.from(selected));
    close();
  }

  async function unlockAll() {
    if (!window.confirm(`Débloquer LES 36 COURS pour ${user.name} ?`)) return;
    await onBulkUnlock(user.id, null);  // null = unlock_all
    close();
  }

  const grouped = useMemo(() => {
    const s = q.trim().toLowerCase();
    const byComp = { 1: [], 2: [], 3: [], 4: [] };
    [...courses]
      .sort((a, b) => (a.competency || 0) - (b.competency || 0) || (a.order || 0) - (b.order || 0))
      .filter(c => {
        if (!s) return true;
        const code = `c${c.competency}-${String(c.order).padStart(2, "0")}`.toLowerCase();
        return code.includes(s) || (c.title || "").toLowerCase().includes(s);
      })
      .forEach(c => { (byComp[c.competency] || (byComp[c.competency] = [])).push(c); });
    return byComp;
  }, [courses, q]);

  const totalShown = Object.values(grouped).reduce((n, list) => n + list.length, 0);
  const lockedCount = courses.length - alreadyUnlocked.size;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`unlock-courses-${user.id}`}
        className="inline-flex items-center gap-1 bg-[#00a86b]/10 hover:bg-[#00a86b]/20 text-[#00a86b] rounded-lg px-2 py-1 text-xs">
        <Unlock className="w-3 h-3" /> Cours
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close} data-testid={`unlock-modal-${user.id}`}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-black/5 bg-gradient-to-r from-[#00a86b]/10 to-white">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-headings font-black text-xl text-[#1a2332]">Débloquer des cours</div>
                  <div className="text-sm text-[#5b6577] mt-1">
                    Élève : <strong className="text-[#1a2332]">{user.name}</strong> · {alreadyUnlocked.size}/{courses.length} déjà débloqués
                  </div>
                </div>
                <button onClick={close} className="text-[#9aa5bc] hover:text-[#e21b3c] text-2xl leading-none px-2" aria-label="Fermer">×</button>
              </div>
            </div>

            <div className="p-4 border-b border-black/5 space-y-2">
              <div className="flex items-center gap-2 bg-[#f8faff] border border-[#e5eaf3] rounded-lg px-3 py-2">
                <Search className="w-3.5 h-3.5 text-[#5b6577]" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher un cours par code ou titre…"
                  className="flex-1 bg-transparent outline-none text-sm text-[#1a2332]"
                  data-testid={`unlock-search-${user.id}`} />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {[1, 2, 3, 4].map(comp => {
                  const compLocked = (courses.filter(c => c.competency === comp && !alreadyUnlocked.has(c.id))).map(c => c.id);
                  return (
                    <button key={comp} onClick={() => {
                      const next = new Set(selected);
                      compLocked.forEach(id => next.add(id));
                      setSelected(next);
                    }}
                      disabled={compLocked.length === 0}
                      className="text-[10px] font-headings font-bold bg-[#00a86b]/10 hover:bg-[#00a86b]/20 disabled:opacity-40 disabled:cursor-not-allowed text-[#00a86b] rounded-md px-2 py-1"
                      data-testid={`unlock-comp-${user.id}-${comp}`}>
                      + Compétence {comp} ({compLocked.length})
                    </button>
                  );
                })}
                <button onClick={() => {
                  const lockedIds = courses.filter(c => !alreadyUnlocked.has(c.id)).map(c => c.id);
                  setSelected(new Set(lockedIds));
                }}
                  disabled={lockedCount === 0}
                  className="text-[10px] font-headings font-bold bg-[#FF8C42]/10 hover:bg-[#FF8C42]/20 disabled:opacity-40 disabled:cursor-not-allowed text-[#FF8C42] rounded-md px-2 py-1"
                  data-testid={`select-all-courses-${user.id}`}>
                  + Tous ({lockedCount})
                </button>
                {selected.size > 0 && (
                  <button onClick={() => setSelected(new Set())}
                    className="text-[10px] text-[#5b6577] hover:text-[#e21b3c] ml-auto">
                    Réinitialiser ×
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingUnlocks && (
                <div className="text-center text-xs text-[#9aa5bc] py-2">Chargement des déblocages existants…</div>
              )}
              {totalShown === 0 && (
                <div className="text-center text-sm text-[#9aa5bc] py-8">Aucun cours ne correspond.</div>
              )}
              {[1, 2, 3, 4].map(comp => {
                const list = grouped[comp] || [];
                if (list.length === 0) return null;
                return (
                  <div key={comp}>
                    <div className="text-[11px] font-headings font-bold text-[#9aa5bc] uppercase tracking-wide px-1 py-1 mb-1">Compétence {comp}</div>
                    <div className="grid sm:grid-cols-2 gap-1">
                      {list.map(c => {
                        const isUnlocked = alreadyUnlocked.has(c.id);
                        const isSel = selected.has(c.id);
                        return (
                          <label key={c.id}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-sm ${
                              isUnlocked
                                ? "bg-[#00a86b]/5 border-[#00a86b]/20 cursor-not-allowed"
                                : isSel
                                  ? "bg-[#FF8C42]/10 border-[#FF8C42]/40 cursor-pointer"
                                  : "bg-white border-black/5 hover:bg-[#f8faff] cursor-pointer"
                            }`}
                            data-testid={`unlock-row-${user.id}-${c.id}`}>
                            <input type="checkbox" checked={isUnlocked || isSel} disabled={isUnlocked}
                              onChange={() => toggle(c.id)} />
                            <span className="font-mono text-[10px] text-[#9aa5bc] shrink-0">{`C${c.competency}-${String(c.order).padStart(2, "0")}`}</span>
                            <span className="text-[#1a2332] flex-1 truncate">{c.title}</span>
                            {isUnlocked && <span className="text-[9px] font-headings font-bold bg-[#00a86b] text-white rounded px-1.5 py-0.5 shrink-0">DÉBLOQUÉ</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-black/5 bg-[#f8faff] flex items-center gap-3 flex-wrap">
              <span className="text-sm text-[#5b6577]">
                <strong className="text-[#FF8C42]">{selected.size}</strong> cours sélectionné(s) à débloquer
              </span>
              <button onClick={unlockAll}
                disabled={lockedCount === 0}
                className="bg-[#00a86b] hover:bg-[#008855] disabled:opacity-40 disabled:cursor-not-allowed text-white font-headings font-bold rounded-xl px-4 py-2.5 text-sm"
                data-testid={`unlock-all-${user.id}`}>
                Tout débloquer ({lockedCount})
              </button>
              <button onClick={applySelection}
                disabled={selected.size === 0}
                className="ml-auto bg-[#FF8C42] hover:bg-[#e8763a] disabled:opacity-40 disabled:cursor-not-allowed text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm"
                data-testid={`unlock-selected-${user.id}`}>
                Débloquer la sélection
              </button>
              <button onClick={close}
                className="bg-white border border-black/5 text-[#1a2332] rounded-xl px-4 py-2.5 text-sm">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NewUserForm({ mentors, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "eleve", transmission: "none", mentor_id: "" });
  const [loading, setLoading] = useState(false);
  const isEleve = f.role === "eleve";
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = { ...f, mentor_id: f.mentor_id || null };
      // Auto-generate password for eleves — backend will create one and email it
      if (isEleve) delete payload.password;
      await http.post("/users", payload);
      toast.success(isEleve ? "Élève créé · mot de passe envoyé par email" : "Créé");
      onDone(); onClose();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-black/5 shadow-sm rounded-2xl p-5 grid md:grid-cols-6 gap-3" data-testid="new-user-form">
      <input required placeholder="Nom" value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="input-admin md:col-span-2" data-testid="new-user-name" />
      <input required type="email" placeholder="Email" value={f.email} onChange={e=>setF({...f,email:e.target.value})} className="input-admin md:col-span-2" data-testid="new-user-email" />
      {!isEleve && (
        <input required type="password" placeholder="Mot de passe (staff)" value={f.password} onChange={e=>setF({...f,password:e.target.value})} className="input-admin md:col-span-2" data-testid="new-user-password" />
      )}
      <select value={f.role} onChange={e=>setF({...f,role:e.target.value})} className={`input-admin ${isEleve ? "md:col-span-2" : ""}`} data-testid="new-user-role">
        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
      {f.role === "eleve" && (
        <>
          <select value={f.transmission} onChange={e=>setF({...f,transmission:e.target.value})} className="input-admin">
            <option value="none">Boîte non définie</option>
            <option value="manual">Manuelle</option>
            <option value="auto">Automatique</option>
          </select>
          <select value={f.mentor_id} onChange={e=>setF({...f,mentor_id:e.target.value})} className="input-admin md:col-span-2">
            <option value="">Pas de mentor</option>
            {mentors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <div className="md:col-span-6 text-xs text-[#5b6577] bg-[#f0fff7] border border-[#00a86b]/20 rounded-lg px-3 py-2">
            <strong>🔐 Mot de passe automatique :</strong> un mot de passe sera généré et envoyé par email à l'élève. Il pourra le changer depuis « Mon compte » ou via « Mot de passe oublié ».
          </div>
        </>
      )}
      <div className="md:col-span-6 flex gap-2">
        <button disabled={loading} className="bg-[#FF8C42] text-white font-headings font-bold rounded-lg px-4 py-2 text-sm" data-testid="new-user-submit">{loading?"…":"Créer"}</button>
        <button type="button" onClick={onClose} className="bg-[#f8faff] border border-black/5 text-[#1a2332] rounded-lg px-4 py-2 text-sm">Annuler</button>
      </div>
      <style>{`.input-admin{background:#f8faff;border:1px solid #e5eaf3;color:#1a2332;padding:.55rem .75rem;border-radius:.6rem;font-size:.85rem;}`}</style>
    </form>
  );
}

function UserProgressBar({ unlocked, total }) {
  const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;
  // Color scale: red <25%, orange <60%, green ≥60%
  const color = pct >= 60 ? "#00a86b" : pct >= 25 ? "#FF8C42" : "#e21b3c";
  return (
    <div className="inline-flex items-center gap-1.5" title={`${unlocked} cours débloqués sur ${total}`}
      data-testid={`progress-bar-${unlocked}-${total}`}>
      <div className="w-20 h-1.5 bg-[#e5eaf3] rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-[10px] font-mono font-bold tabular-nums" style={{ color }}>
        {unlocked}/{total}
      </span>
    </div>
  );
}

